import {
  RHYTHM_PITCH_KEPT_DAYS,
  type AdminRhythmOverview,
  type RhythmBaselineArm,
} from '@steado/admin-contract/rhythm';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Panel, StatCards, TabSkeleton } from '@/components/panel/panels';
import { BadgeCell } from '@/components/table/cells';
import { TONE_DOTS } from '@/components/table/tone';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';

import { BaselinePanel } from './BaselinePanel';
import { STATE_TONES, count, localMinute, shareCounts, sharePercent } from './rhythm-format';
import { rhythmOverviewQueryOptions } from './rhythm-query';

/** Weekly actives at which the holdout is shrunk, once held for two weeks. */
const HOLDOUT_SHRINK_AT = 2000;

/** The share of recording installations sending sessions at which signals (phase 1) is done. */
const SIGNALS_EXIT_PERCENT = 80;

const percentOf = (part: number, whole: number): string =>
  whole === 0 ? '—' : `${String(Math.round((part / whole) * 100))}%`;

/**
 * Rhythm: who is in which arm, where each account is in its life with the
 * app, and the baseline the engine will be judged against.
 *
 * "Rhythm" is the engine's internal name and appears only here in the
 * console. Users see "streak" and "weeks", and none of this reaches the app.
 */
export function RhythmScreen() {
  const { api } = useAuth();
  const [arm, setArm] = useState<RhythmBaselineArm>('all');
  // The rest of the screen stays put while another arm's baseline loads.
  const query = useQuery({ ...rhythmOverviewQueryOptions(api, arm), placeholderData: keepPreviousData });
  const overview = query.data;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3.5">
      <p className="max-w-[680px] text-[13px] leading-relaxed text-muted-foreground">
        Who is still showing up, and the numbers a change to reminders is measured against. A share of users
        is held out so there is someone to compare with. Nothing here is shown to users or sent to analytics.
      </p>

      {query.isPending ? <TabSkeleton rows={4} /> : null}
      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {overview === undefined ? null : (
        <>
          {overview.enabled ? null : (
            <Alert role="note" tone="warn">
              <AlertDescription>
                Rhythm is switched off, so no user is being given an arm or a state and those counts stand
                still. Weekly actives and the baseline come from the logs and are current. Set
                RHYTHM_HOLDOUT_PERCENT on the server before turning it on in Feature switches.
              </AlertDescription>
            </Alert>
          )}

          <StatCards
            stats={[
              {
                label: 'Weekly actives',
                value: count(overview.weeklyActives),
                note: `Opened the app or logged in the last 7 days. The holdout shrinks to 10% after two weeks above ${count(HOLDOUT_SHRINK_AT)}.`,
              },
              {
                label: 'Placed',
                value: count(overview.profiles),
                note: `of ${count(overview.users)} accounts have an arm and a state.`,
              },
              {
                label: 'Rhythm arm',
                value: count(overview.arms.rhythm),
                note: `${percentOf(overview.arms.rhythm, overview.profiles)} of placed users.`,
              },
              {
                label: 'Holdout',
                value: count(overview.arms.holdout),
                note: `${percentOf(overview.arms.holdout, overview.profiles)} of placed users. New profiles are held out at ${String(overview.holdoutPercent)}%.`,
              },
              overview.sessionRecording === null
                ? {
                    label: 'Sending sessions',
                    value: '—',
                    note: 'No app build that records sessions is out yet.',
                  }
                : {
                    label: 'Sending sessions',
                    value: sharePercent(overview.sessionRecording),
                    note: `${shareCounts(overview.sessionRecording)} installations seen in the last 7 days on a build that records. ${String(SIGNALS_EXIT_PERCENT)}% is the bar.`,
                  },
            ]}
          />

          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.25fr_1fr]">
            <StatesPanel overview={overview} />
            <TransitionsPanel overview={overview} />
          </div>

          <TimingPanel overview={overview} />

          <PitchPanel overview={overview} />

          <BaselinePanel weeks={overview.baseline} arm={arm} onArmChange={setArm} />
        </>
      )}
    </div>
  );
}

/**
 * Every state, present ones first, as a share of the users Rhythm has placed.
 * All seven are listed with their zeros: a state missing from the list and a
 * state nobody is in must not look the same.
 */
function StatesPanel({ overview }: { readonly overview: AdminRhythmOverview }) {
  return (
    <Panel
      title="Where users are"
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          Slipping is 2 to 6 days away, lapsed 7 to 29, dormant 30 or more. Returning is the first three days
          back after a week or more away. Steady is two or more kept weeks in a row, counted by the streak
          ledger.
        </p>
      }
    >
      <div className="flex flex-col">
        {overview.states.map(({ state, count: users }) => {
          const share = overview.profiles === 0 ? 0 : Math.round((users / overview.profiles) * 100);
          return (
            <div key={state} className="flex flex-col gap-1.5 py-[7px]">
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-medium text-foreground capitalize">{state}</span>
                <span className="ml-auto font-mono text-[13px] text-foreground">{count(users)}</span>
                <span className="w-[52px] text-right font-mono text-[13px] text-muted-foreground">
                  {String(share)}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                <div
                  className={cn('h-full rounded-full', TONE_DOTS[STATE_TONES[state]])}
                  style={{ width: `${String(share)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

const PITCH_LABELS: Readonly<Record<AdminRhythmOverview['pitches']['hooks'][number]['hook'], string>> = {
  workout_reminders: 'After a workout: reminders',
  weekly_summary: 'After a weekly review: summaries',
  streak: 'At a milestone: reminders',
};

/** Accepted over shown at which the pitch (phase 5) is doing its job. */
const PITCH_EXIT_PERCENT = 15;

/**
 * The permission pitch: offers to switch a notification category on, by the
 * moment the app makes them. Counts, with the rate beside them only where
 * there is something to divide by.
 */
function PitchPanel({ overview }: { readonly overview: AdminRhythmOverview }) {
  const { pitches } = overview;
  return (
    <Panel
      title="Permission pitch"
      description={
        pitches.enabled
          ? 'On: the app is handed an offer when asking is worth it.'
          : 'Off: no offer is made. Earlier ones stay counted.'
      }
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          An offer is for a Rhythm-arm user with the category off, at most one at a time, none for 21 days
          after one they saw, three for a category ever. The bar is {String(PITCH_EXIT_PERCENT)}% accepted of
          those shown, with the category still on {String(RHYTHM_PITCH_KEPT_DAYS)} days later for four in
          five. Still on is where the switch stands today.
        </p>
      }
    >
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th className="py-1.5 font-normal">When</th>
            <th className="py-1.5 text-right font-normal">Offered</th>
            <th className="py-1.5 text-right font-normal">Shown</th>
            <th className="py-1.5 text-right font-normal">Accepted</th>
            <th className="py-1.5 text-right font-normal">Dismissed</th>
            <th className="py-1.5 text-right font-normal">Still on</th>
          </tr>
        </thead>
        <tbody>
          {pitches.hooks.map((row) => (
            <tr key={row.hook} className="border-t border-border">
              <td className="py-[7px] text-foreground">{PITCH_LABELS[row.hook]}</td>
              <td className="py-[7px] text-right font-mono">{count(row.offered)}</td>
              <td className="py-[7px] text-right font-mono">{count(row.shown)}</td>
              <td className="py-[7px] text-right font-mono">
                {count(row.accepted)}
                <span className="ml-1.5 text-muted-foreground">{percentOf(row.accepted, row.shown)}</span>
              </td>
              <td className="py-[7px] text-right font-mono">{count(row.dismissed)}</td>
              <td className="py-[7px] text-right font-mono">
                {sharePercent(row.stillOn)}
                <span className="ml-1.5 text-muted-foreground">{shareCounts(row.stillOn)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

/**
 * Personal send times for the Rhythm arm: how many have their own, and where
 * the evening ones fall. Worked out daily whether or not they are switched on,
 * so this is read before they are.
 */
function TimingPanel({ overview }: { readonly overview: AdminRhythmOverview }) {
  const { timing } = overview;
  const busiest = Math.max(1, ...timing.evening.map((row) => row.count));
  const slots = [
    { label: 'Evening nudge', share: timing.personal.evening },
    { label: 'Rest-day tip', share: timing.personal.morning },
    { label: 'Sunday summary', share: timing.personal.sunday },
  ];

  return (
    <Panel
      title="Personal times"
      description={
        timing.enabled
          ? 'On: these times are in the Rhythm arm’s schedule.'
          : 'Off: everyone gets the fixed times. These are what the Rhythm arm would get.'
      }
      footer={
        <p className="text-xs text-pretty text-muted-foreground">
          Worked out once a day from the last eight weeks of opens and logs. Without five on three days in the
          last four weeks a slot keeps its default: 20:00, 10:30 and 19:15. The holdout keeps the defaults
          whatever its times say.
        </p>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <dl className="flex flex-col gap-2.5">
          {slots.map((slot) => (
            <div key={slot.label} className="flex items-baseline gap-2">
              <dt className="text-[13px] text-muted-foreground">{slot.label}</dt>
              <dd className="ml-auto font-mono text-[13px] text-foreground">{sharePercent(slot.share)}</dd>
              <dd className="w-[120px] text-right text-xs text-muted-foreground">
                {shareCounts(slot.share)}
              </dd>
            </div>
          ))}
        </dl>
        {timing.evening.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No evening times yet.</p>
        ) : (
          <ul className="flex flex-col gap-1" aria-label="Evening times">
            {timing.evening.map((row) => (
              <li key={row.minute} className="flex items-center gap-2">
                <span className="w-11 font-mono text-[12.5px] text-muted-foreground">
                  {localMinute(row.minute)}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <span
                    className="block h-full rounded-full bg-info-fg"
                    style={{ width: `${String(Math.round((row.count / busiest) * 100))}%` }}
                  />
                </span>
                <span className="w-10 text-right font-mono text-[12.5px] text-foreground">
                  {count(row.count)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/** State changes by pair. A user's first placement has nowhere it came from. */
function TransitionsPanel({ overview }: { readonly overview: AdminRhythmOverview }) {
  return (
    <Panel title="How states moved" bleed>
      {overview.transitions.length === 0 ? (
        <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
          No state has changed in the last 28 days.
        </p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-t border-border text-xs font-medium text-muted-foreground">
              <th scope="col" className="px-[18px] py-2 text-left font-medium">
                Change
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                7 days
              </th>
              <th scope="col" className="py-2 pr-[18px] pl-3 text-right font-medium">
                28 days
              </th>
            </tr>
          </thead>
          <tbody>
            {overview.transitions.map((row) => (
              <tr key={`${row.from ?? 'first'}:${row.to}`} className="border-t border-border">
                <th scope="row" className="px-[18px] py-2 text-left font-normal">
                  <span className="flex items-center gap-1.5">
                    {row.from === null ? (
                      <span className="text-[13px] text-muted-foreground">first placed</span>
                    ) : (
                      <BadgeCell label={row.from} tone={STATE_TONES[row.from]} />
                    )}
                    <span aria-hidden="true" className="text-muted-foreground">
                      →
                    </span>
                    <span className="sr-only">to</span>
                    <BadgeCell label={row.to} tone={STATE_TONES[row.to]} />
                  </span>
                </th>
                <td className="px-3 py-2 text-right font-mono text-[13px] text-foreground">
                  {count(row.last7Days)}
                </td>
                <td className="py-2 pr-[18px] pl-3 text-right font-mono text-[13px] text-foreground">
                  {count(row.last28Days)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
